"use client";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

/**
 * Context value for managing collapse state across multiple cards
 */
export type CollapseContextValue = {
  /** Set of card IDs that are currently collapsed */
  collapsedCards: Set<string>;
  /** Toggle collapse state for a specific card */
  toggleCard: (cardId: string) => void;
  /** Collapse all registered cards */
  collapseAll: () => void;
  /** Expand all registered cards */
  expandAll: () => void;
  /** Set of all registered card IDs */
  registeredCards: Set<string>;
  /** Register a card with the context */
  registerCard: (cardId: string) => void;
  /** Unregister a card from the context */
  unregisterCard: (cardId: string) => void;
  /** Check if a specific card is collapsed */
  isCollapsed: (cardId: string) => boolean;
};

const CollapseContext = createContext<CollapseContextValue | null>(null);

/**
 * Hook to access the collapse context
 * Returns null if not within a CollapseProvider
 */
export function useCollapseContext(): CollapseContextValue | null {
  return useContext(CollapseContext);
}

/**
 * Provider component for managing collapse state
 */
export function CollapseProvider({ children }: { children: ReactNode }) {
  const [collapsedCards, setCollapsedCards] = useState<Set<string>>(new Set());
  const [registeredCards, setRegisteredCards] = useState<Set<string>>(
    new Set()
  );

  const toggleCard = useCallback((cardId: string) => {
    setCollapsedCards((prev) => {
      const next = new Set(prev);
      if (next.has(cardId)) {
        next.delete(cardId);
      } else {
        next.add(cardId);
      }
      return next;
    });
  }, []);

  const collapseAll = useCallback(() => {
    setCollapsedCards(new Set(registeredCards));
  }, [registeredCards]);

  const expandAll = useCallback(() => {
    setCollapsedCards(new Set());
  }, []);

  const registerCard = useCallback((cardId: string) => {
    setRegisteredCards((prev) => {
      if (prev.has(cardId)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(cardId);
      return next;
    });
  }, []);

  const unregisterCard = useCallback((cardId: string) => {
    setRegisteredCards((prev) => {
      if (!prev.has(cardId)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(cardId);
      return next;
    });
    // Also remove from collapsed if it was collapsed
    setCollapsedCards((prev) => {
      if (!prev.has(cardId)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(cardId);
      return next;
    });
  }, []);

  const isCollapsed = useCallback(
    (cardId: string) => collapsedCards.has(cardId),
    [collapsedCards]
  );

  const value = useMemo<CollapseContextValue>(
    () => ({
      collapsedCards,
      toggleCard,
      collapseAll,
      expandAll,
      registeredCards,
      registerCard,
      unregisterCard,
      isCollapsed,
    }),
    [
      collapsedCards,
      toggleCard,
      collapseAll,
      expandAll,
      registeredCards,
      registerCard,
      unregisterCard,
      isCollapsed,
    ]
  );

  return (
    <CollapseContext.Provider value={value}>
      {children}
    </CollapseContext.Provider>
  );
}
